import React, { useState, useEffect } from 'react'
import { Box, Paper, Typography, Button, Container, Card, CardContent, Avatar, Chip, IconButton, Alert, CircularProgress } from '@mui/material'
import { Face as FaceIcon, CheckCircle as CheckCircleIcon, ArrowBack as ArrowBackIcon, LocationOn as LocationIcon, LocationOff as LocationOffIcon } from '@mui/icons-material'
import { useFaceIO } from '../../context/FaceIOContext'
import toast from 'react-hot-toast'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { attendanceApi } from '../../api/attendanceApi'
import { organizationApi } from '../../api/organizationApi'
import { getTimestampISO } from '../../utils/formatters'
import { getCurrentPosition, isWithinGeofence } from '../../utils/geofence'

export default function TestFaceAttendance() {
    const { faceioInstance, error: faceIoError } = useFaceIO()
    const [currentUser, setCurrentUser] = useState(null)
    const [geofenceConfig, setGeofenceConfig] = useState(null)   // { latitude, longitude, geofence_radius }
    const [geofenceLoading, setGeofenceLoading] = useState(true)
    const [geofenceError, setGeofenceError] = useState(null)     // 'no_config' | 'fetch_failed'
    const [isProcessing, setIsProcessing] = useState(false)

    const location = useLocation()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const { user, orgId } = useSelector((state) => state.auth)

    // org-id from QR scan URL takes priority over Redux orgId
    const qrOrgId = searchParams.get('org-id')

    // Fetch org geofence config on mount
    useEffect(() => {
        const fetchGeofence = async () => {
            setGeofenceLoading(true)
            try {
                const response = await organizationApi.getOrgGeofenceInfo(qrOrgId || orgId)
                const data = response.data
                // latitude=0 & longitude=0 is the API's sentinel meaning "not configured yet"
                const lat = parseFloat(data?.latitude)
                const lng = parseFloat(data?.longitude)
                const hasConfig = data && !isNaN(lat) && !isNaN(lng) && !(lat === 0 && lng === 0)

                if (hasConfig) {
                    setGeofenceConfig({
                        latitude: lat,
                        longitude: lng,
                        geofence_radius: parseInt(data.geofence_radius) || 100,
                    })
                } else {
                    // No geofence configured — allow check-in but show info
                    setGeofenceError('no_config')
                }
            } catch (error) {
                console.warn('Geofence info not available:', error)
                // If endpoint not ready yet, fall back gracefully (allow check-in)
                setGeofenceError('fetch_failed')
            } finally {
                setGeofenceLoading(false)
            }
        }
        fetchGeofence()
    }, [])

    const handleCheckIn = async () => {
        if (!faceioInstance) {
            toast.error("FaceIO not initialized")
            return
        }

        setIsProcessing(true)

        try {
            // ── Step 1: Geofence Validation ──────────────────────────────────────
            let employeeLocation = null

            if (geofenceConfig) {
                // Config exists → validate location
                let currentPos
                try {
                    currentPos = await getCurrentPosition()
                } catch (locErr) {
                    setIsProcessing(false)
                    if (locErr.message === 'PERMISSION_DENIED') {
                        toast.error('Location access denied. Please allow location permissions to mark attendance.', { duration: 5000 })
                    } else if (locErr.message === 'GEOLOCATION_NOT_SUPPORTED') {
                        toast.error('Your browser does not support location services.')
                    } else {
                        toast.error('Unable to get your location. Please try again.')
                    }
                    return
                }

                const within = isWithinGeofence(
                    currentPos.latitude,
                    currentPos.longitude,
                    geofenceConfig.latitude,
                    geofenceConfig.longitude,
                    geofenceConfig.geofence_radius
                )

                if (!within) {
                    setIsProcessing(false)
                    toast.error(
                        `You are outside the office zone (radius: ${geofenceConfig.geofence_radius}m). Check-in is not allowed.`,
                        { duration: 6000, icon: '📍' }
                    )
                    return
                }

                employeeLocation = currentPos
            } else {
                // No geofence config — silently try to get location for logging only
                try {
                    employeeLocation = await getCurrentPosition()
                } catch (_) {
                    // Ignore if location not available when no geofence configured
                }
            }

            // ── Step 2: Face Authentication ──────────────────────────────────────
            const userData = await faceioInstance.authenticate({
                locale: "auto",
                permissionTimeout: 5,
                idleTimeout: 10,
                replyTimeout: 10
            })
            console.log("Authentication Success:", userData)

            // ── Step 3: Fetch Attendance Log to Determine Punch Type ─────────────
            let punchType = 'check-in' // default
            try {
                // Using fetch to avoid axios interceptors redirecting non-logged-in QR scanners to /login 
                const baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://facedevapi.intellisiatechnologies.com';
                const response = await fetch(`${baseUrl}/employee/attendance/today`, {
                    headers: {
                        'X-Employee-ID': userData.payload.id,
                        'X-Org-ID': qrOrgId || orgId || ''
                    }
                })
                
                if (response.ok) {
                    const resData = await response.json()
                    // The actual attendance record is either the top level object or wrapped in `data` depending on backend router standard.
                    // Assuming the data shape checked in Attendance.jsx: `resData.data` might be the payload or `resData` might just be it
                    const data = resData.data || resData
                    
                    if (data && (data.check_in || data.check_out)) {
                        const hasCheckIn = data.check_in && data.check_in !== '-'
                        const hasCheckOut = data.check_out && data.check_out !== '-'
                        
                        if (hasCheckIn && hasCheckOut) {
                            // Already checked in and out for today
                            setIsProcessing(false)
                            toast.error('You have already completed your check-in and check-out for today.', { duration: 5000 })
                            faceioInstance.restartSession()
                            setTimeout(() => {
                                if (!user) setCurrentUser(null)
                            }, 5000)
                            return
                        } else if (hasCheckIn && !hasCheckOut) {
                            punchType = 'check-out'
                        } else {
                            punchType = 'check-in'
                        }
                    }
                } else if (response.status !== 404) {
                    console.warn("Could not fetch today's attendance log, defaulting to check-in", response.status)
                }
            } catch (fetchErr) {
                console.warn("Error fetching today's log:", fetchErr)
            }

            setCurrentUser({
                employeeId: userData.payload.id,
                name: userData.payload.name,
                department: userData.payload.department,
                employeeCode: userData.payload.employeeId,
                facialId: userData.facialId,
                timestamp: new Date().toLocaleTimeString(),
                detectedAction: punchType // Save exactly what happened (check-in/check-out)
            })

            // ── Step 4: Mark Attendance ───────────────────────────────────────────
            const apiPayload = {
                punch_type: punchType,
                timestamp: getTimestampISO(),
                location: "Office",
                facial_id: userData.facialId,
                ...(employeeLocation && {
                    latitude: employeeLocation.latitude,
                    longitude: employeeLocation.longitude,
                }),
            }

            console.log("Sending Attendance Payload:", apiPayload)

            // Use raw fetch for QR / unauthenticated users to bypass the axios
            // 401 interceptor (which would clear localStorage and redirect to login).
            // Logged-in employees can use the axios instance normally.
            if (!user) {
                const baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://facedevapi.intellisiatechnologies.com'
                const markRes = await fetch(`${baseUrl}/employee/attendance/mark`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Employee-ID': userData.payload.id,
                        'X-Org-ID': qrOrgId || orgId || '',
                    },
                    body: JSON.stringify(apiPayload),
                })
                if (!markRes.ok) {
                    const errData = await markRes.json().catch(() => ({}))
                    throw new Error(errData?.message || errData?.detail || `Server error ${markRes.status}`)
                }
            } else {
                await attendanceApi.markAttendance(apiPayload)
            }

            const successMessage = punchType === 'check-in'
                ? `Welcome back, ${userData.payload.name}! Checked In successfully.`
                : `Goodbye, ${userData.payload.name}! Checked Out successfully.`

            toast.success(successMessage)
            faceioInstance.restartSession()

            setTimeout(() => {
                if (user) {
                    // Logged-in employee: stay on the attendance page (no login redirect)
                    navigate('/employee/attendance')
                } else {
                    // QR / unauthenticated: reset state so the next person can scan
                    setCurrentUser(null)
                }
            }, 3000)

        } catch (errCode) {
            console.error("Authentication Failed:", errCode)
            handleError(errCode)
            faceioInstance.restartSession()
        } finally {
            setIsProcessing(false)
        }
    }

    const handleError = (errCode) => {
        let msg = "Authentication failed"

        switch (errCode) {
            case fioErrCode.PERMISSION_REFUSED:
                msg = 'Camera access denied. Please allow camera permissions and try again.'
                break
            case fioErrCode.NO_FACES_DETECTED:
                msg = 'No face detected. Please position your face clearly in the camera frame.'
                break
            case fioErrCode.UNRECOGNIZED_FACE:
                msg = 'Face not recognized. Please ensure you are enrolled in the system.'
                break
            case fioErrCode.MANY_FACES:
                msg = 'Multiple faces detected. Please ensure only one person is in the frame.'
                break
            case fioErrCode.PAD_ATTACK:
                msg = 'Presentation attack detected. Please use a live face, not a photo or video.'
                break
            case fioErrCode.FACE_MISMATCH:
                msg = 'Facial features do not match. Please try again.'
                break
            case fioErrCode.WRONG_PIN_CODE:
                msg = 'Incorrect PIN code entered.'
                break
            case fioErrCode.PROCESSING_ERR:
                msg = 'Server processing error. Please try again later.'
                break
            case fioErrCode.UNAUTHORIZED:
                msg = 'Application is not authorized. Please contact support.'
                break
            case fioErrCode.TERMS_NOT_ACCEPTED:
                msg = 'Terms and conditions must be accepted to proceed.'
                break
            case fioErrCode.UI_NOT_READY:
                msg = 'FaceIO widget is not ready. Please refresh the page and try again.'
                break
            case fioErrCode.SESSION_EXPIRED:
                msg = 'Session expired. Please try authentication again.'
                break
            case fioErrCode.TIMEOUT:
                msg = 'Operation timed out. Please try again.'
                break
            case fioErrCode.TOO_MANY_REQUESTS:
                msg = 'Too many requests. Please wait a moment and try again.'
                break
            case fioErrCode.EMPTY_ORIGIN:
                msg = 'Invalid request origin. Please contact support.'
                break
            case fioErrCode.FORBIDDEN_ORIGIN:
                msg = 'This domain is not authorized to use FaceIO.'
                break
            case fioErrCode.FORBIDDEN_COUNTRY:
                msg = 'FaceIO is not available in your country.'
                break
            case fioErrCode.SESSION_IN_PROGRESS:
                msg = 'Another authentication session is already in progress.'
                break
            case fioErrCode.NETWORK_IO:
                msg = 'Network connection error. Please check your internet and try again.'
                break
            default:
                msg = `Authentication failed: ${errCode}`
                break
        }

        toast.error(msg)
    }

    // ── Geofence Status Banner ──────────────────────────────────────────────────
    const renderGeofenceBanner = () => {
        if (geofenceLoading) {
            return (
                <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mb: 3 }}>
                    Verifying office location...
                </Alert>
            )
        }
        if (geofenceConfig) {
            return (
                <Alert
                    severity="success"
                    icon={<LocationIcon fontSize="small" />}
                    sx={{ mb: 3 }}
                >
                    Geofencing active — radius {geofenceConfig.geofence_radius}m. Your location will be checked before scanning.
                </Alert>
            )
        }
        if (geofenceError === 'no_config') {
            return (
                <Alert severity="warning" icon={<LocationOffIcon fontSize="small" />} sx={{ mb: 3 }}>
                    No office location set. Contact your administrator to enable geofencing.
                </Alert>
            )
        }
        // fetch_failed or other — allow check-in but inform
        return null
    }

    return (
        <Container maxWidth="sm" sx={{
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 0
        }}>
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center', position: 'relative', width: '100%' }}>
                <IconButton
                    onClick={() => navigate(-1)}
                    sx={{ position: 'absolute', left: 16, top: 16 }}
                >
                    <ArrowBackIcon />
                </IconButton>

                <Box sx={{ mb: 4 }}>
                    <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main', mx: 'auto', mb: 2 }}>
                        <FaceIcon fontSize="large" />
                    </Avatar>
                    <Typography variant="h4" fontWeight="bold" gutterBottom>
                        Face Attendance
                    </Typography>
                    <Typography color="textSecondary">
                        Click the button below to scan your face
                    </Typography>
                </Box>

                {/* Geofence status banner */}
                {renderGeofenceBanner()}

                {faceIoError ? (
                    <Typography color="error" sx={{ mb: 3 }}>
                        System Error: {faceIoError}
                    </Typography>
                ) : (
                    !currentUser ? (
                        <Button
                            variant="contained"
                            size="large"
                            color="primary"
                            onClick={handleCheckIn}
                            disabled={isProcessing || geofenceLoading}
                            sx={{ px: 4, py: 1.5, borderRadius: 2 }}
                            startIcon={isProcessing ? <CircularProgress size={18} color="inherit" /> : null}
                        >
                            {isProcessing
                                ? 'Processing...'
                                : `Scan Face to Mark Attendance`
                            }
                        </Button>
                    ) : null
                )}

                {currentUser && (
                    <Card sx={{ mt: 4, textAlign: 'left', border: '1px solid #4caf50' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                                <Typography variant="h6" color="success.main">
                                    Verified Successfully
                                </Typography>
                            </Box>

                            <Typography variant="body1"><strong>Name:</strong> {currentUser.name}</Typography>
                            <Typography variant="body1"><strong>ID:</strong> {currentUser.employeeId}</Typography>
                            <Typography variant="body1"><strong>Department:</strong> {currentUser.department}</Typography>
                            <Typography variant="body1"><strong>Confidence:</strong> High</Typography>
                            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                                Facial ID: {currentUser.facialId}
                            </Typography>
                            <Chip
                                label={`${currentUser.detectedAction === 'check-in' ? 'Checked In' : 'Checked Out'} at ${currentUser.timestamp}`}
                                color={currentUser.detectedAction === 'check-in' ? 'primary' : 'secondary'}
                                size="small"
                                sx={{ mt: 2 }}
                            />
                            {user && (
                                <Typography variant="caption" display="block" sx={{ mt: 2, fontStyle: 'italic' }}>
                                    Redirecting to attendance page...
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                )}
            </Paper>
        </Container>
    )
}
